import { TriangleAlert } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { needsApi, servicesApi } from '../api/endpoints'
import type { ProfileResponse } from '../types/api'

/**
 * The same "you haven't added X yet" warning NeedsSection/ServicesSection already show inline on
 * the profile page - surfaced here too, on every page right after sign-in, so acting on it doesn't
 * take knowing to scroll down /profile first. Hidden on /profile itself, where the inline version
 * sits right next to the button that fixes it and a second copy would just be noise.
 */
export function SetupReminderBanner({ profile }: { profile: ProfileResponse }) {
  const location = useLocation()
  const [loading, setLoading] = useState(true)
  const [hasSomethingAdded, setHasSomethingAdded] = useState(true)

  const isSupply = profile.persona === 'SUPPLY'
  const isGuardian = profile.entityKind === 'GUARDIAN'

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    const request = isSupply ? servicesApi.listOwn() : needsApi.listOwn()
    request
      .then((rows) => {
        if (!cancelled) setHasSomethingAdded(rows.length > 0)
      })
      .catch(() => {
        // Unknown is treated as "don't nag" - a failed fetch is not evidence of an empty profile,
        // and a banner that can misfire on a network hiccup is worse than one that stays quiet.
        if (!cancelled) setHasSomethingAdded(true)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [profile.id, isSupply])

  // Each persona's inline version of this warning lives on a different page: NeedsSection is
  // part of /profile, but ServicesSection is its own page at /services (see ServicesPage) rather
  // than a section within /profile.
  const onOwnPage = isSupply ? location.pathname === '/services' : location.pathname === '/profile'
  if (loading || hasSomethingAdded || onOwnPage) return null

  const copy = isSupply
    ? {
        text: 'You have not added what you teach yet.',
        anchor: '/services',
        cta: 'Add what you teach',
      }
    : isGuardian
      ? {
          text: 'You have not added what your child wants to learn yet.',
          anchor: '/profile#needs',
          cta: 'Add what your child wants to learn',
        }
      : {
          text: 'You have not added what you want to learn yet.',
          anchor: '/profile#needs',
          cta: 'Add what you want to learn',
        }

  return (
    <div className="border-b border-sun-400/50 bg-sun-400/10 px-4 py-2.5">
      <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-2 text-sm text-amber-900">
        <TriangleAlert size={15} className="shrink-0" />
        <span className="min-w-0 flex-1">{copy.text} The map can only match you once you have.</span>
        <Link
          to={copy.anchor}
          className="shrink-0 font-semibold underline underline-offset-2 hover:no-underline"
        >
          {copy.cta}
        </Link>
      </div>
    </div>
  )
}
